import { parseDateOnly, startOfDay } from './assigned-queue';

/** The team is in the UK, so dates read day-first whatever the browser says. */
export const APP_LOCALE = 'en-GB';

const DATE_OPTIONS: Intl.DateTimeFormatOptions = { dateStyle: 'medium' };
const DATETIME_OPTIONS: Intl.DateTimeFormatOptions = {
  dateStyle: 'medium',
  timeStyle: 'short',
};
const DAY_MS = 24 * 60 * 60 * 1000;

/** Calendar date, always including the year, e.g. "23 Oct 2026". */
export function formatDisplayDate(value: Date, locale: string = APP_LOCALE): string {
  return new Intl.DateTimeFormat(locale, DATE_OPTIONS).format(value);
}

/** ISO timestamp with date and time, year included. */
export function formatDisplayDateTime(value: Date, locale: string = APP_LOCALE): string {
  return new Intl.DateTimeFormat(locale, DATETIME_OPTIONS).format(value);
}

/** Whole days from `today` to `value`; negative once it has passed. */
export function daysBetween(today: Date, value: Date): number {
  return Math.round((startOfDay(value).getTime() - startOfDay(today).getTime()) / DAY_MS);
}

/**
 * A date the way people say it: "Today", "Tomorrow", "Fri 23 Oct",
 * or "23 Oct 2025" when it is not this year.
 */
export function formatFriendlyDate(value: Date, today: Date = new Date(), locale: string = APP_LOCALE): string {
  const days = daysBetween(today, value);
  if (days === 0) return 'Today';
  if (days === 1) return 'Tomorrow';
  if (days === -1) return 'Yesterday';
  if (value.getFullYear() !== today.getFullYear()) {
    return new Intl.DateTimeFormat(locale, { day: 'numeric', month: 'short', year: 'numeric' }).format(value);
  }
  return new Intl.DateTimeFormat(locale, { weekday: 'short', day: 'numeric', month: 'short' }).format(value);
}

/** YYYY-MM-DD → friendly date, or null when it does not parse. */
export function formatFriendlyDateOnly(value: string | null | undefined, today: Date = new Date()): string | null {
  if (!value) return null;
  const parsed = parseDateOnly(value);
  return parsed ? formatFriendlyDate(parsed, today) : null;
}

/** "3 days overdue", "1 day overdue", or null when the date has not passed. */
export function lateText(dueDate: string | null | undefined, today: Date = new Date()): string | null {
  if (!dueDate) return null;
  const parsed = parseDateOnly(dueDate);
  if (!parsed) return null;
  const days = daysBetween(today, parsed);
  if (days >= 0) return null;
  return `${-days} ${days === -1 ? 'day' : 'days'} overdue`;
}

/** "in 3 days", "today", "3 days ago", for how long something has waited or is left. */
export function relativeDays(value: Date, today: Date = new Date()): string {
  const days = daysBetween(today, value);
  if (days === 0) return 'today';
  if (days === 1) return 'tomorrow';
  if (days === -1) return 'yesterday';
  return days > 0 ? `in ${days} days` : `${-days} days ago`;
}

/** Whole days since an ISO timestamp, never negative. */
export function daysSince(iso: string | null | undefined, today: Date = new Date()): number | null {
  if (!iso) return null;
  const parsed = new Date(iso);
  if (Number.isNaN(parsed.getTime())) return null;
  return Math.max(0, -daysBetween(today, parsed));
}

/** Format a YYYY-MM-DD work-item date without UTC-shifting the day. */
export function formatDateOnly(value: string, locale: string = APP_LOCALE): string | null {
  const parsed = parseDateOnly(value);
  if (!parsed) return null;
  return formatDisplayDate(parsed, locale);
}

/** ISO timestamp → locale calendar date with year (no time). */
export function formatTimestampDate(value: string, locale: string = APP_LOCALE): string | null {
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return null;
  return formatDisplayDate(parsed, locale);
}

/** ISO timestamp → locale date and time. */
export function formatTimestampDateTime(value: string, locale: string = APP_LOCALE): string | null {
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return null;
  return formatDisplayDateTime(parsed, locale);
}

/** Monday of the local week that contains `today`. */
export function startOfLocalWeek(today: Date): Date {
  const start = startOfDay(today);
  const weekday = start.getDay();
  const daysFromMonday = weekday === 0 ? 6 : weekday - 1;
  return new Date(start.getFullYear(), start.getMonth(), start.getDate() - daysFromMonday);
}

/** Sunday of the local week that contains `today`. */
export function endOfLocalWeek(today: Date): Date {
  const start = startOfLocalWeek(today);
  return new Date(start.getFullYear(), start.getMonth(), start.getDate() + 6);
}

/** Monday–Sunday range with year, e.g. "31 Aug–6 Sept 2026". */
export function formatCalendarWeek(day: Date, locale: string = APP_LOCALE): string {
  const start = startOfLocalWeek(day);
  const end = endOfLocalWeek(start);
  const sameMonth = start.getMonth() === end.getMonth();
  const sameYear = start.getFullYear() === end.getFullYear();
  const startText = new Intl.DateTimeFormat(locale, {
    day: 'numeric',
    ...(sameMonth ? {} : { month: 'short' }),
    ...(sameYear ? {} : { year: 'numeric' }),
  }).format(start);
  const endText = new Intl.DateTimeFormat(locale, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  }).format(end);
  return `${startText}–${endText}`;
}

/** Compact Monday–Sunday range for the week that contains `today`. */
export function formatThisWeekRange(today: Date, locale: string = APP_LOCALE): string {
  return formatCalendarWeek(today, locale);
}

export function addWeeks(day: Date, weeks: number): Date {
  if (!Number.isInteger(weeks) || weeks < 0) {
    throw new Error('Week offset must be an integer of 0 or more.');
  }
  return shiftWeeks(day, weeks);
}

/** Shift a local date by a whole number of weeks. Negative moves earlier. */
export function shiftWeeks(day: Date, weeks: number): Date {
  if (!Number.isInteger(weeks)) {
    throw new Error('Week offset must be an integer.');
  }
  const start = startOfDay(day);
  return new Date(start.getFullYear(), start.getMonth(), start.getDate() + weeks * 7);
}

/** Monday of the week as YYYY-MM-DD. */
export function formatWeekStart(day: Date): string {
  const monday = startOfLocalWeek(day);
  const year = monday.getFullYear();
  const month = String(monday.getMonth() + 1).padStart(2, '0');
  const date = String(monday.getDate()).padStart(2, '0');
  return `${year}-${month}-${date}`;
}
